import { faChevronDown, faInfoCircle, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider } from "../components/ui/divider";
import { PriceProps, PriceResultAllExtra, PriceResultProps, StationResultProps } from "@/lib/type";
import { useSearchParams } from "react-router-dom";
import { HIDDEN_EXTRA_ITEMS, ONE_TIME_CHARGE_EXTRA_ITEMS } from "@/lib/constant";
import { extraPriceWithCountOnly, extraPriceWithRentalDays } from "@/helpers/singleExtraPrice";
import { extraTotalWithCountOnly, extraTotalWithRentalDays } from "@/helpers/totalExtraPrice";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip } from "@/components/ui/tooltip";
import { memo, useMemo } from "react";
import { formatCurrencySymbol, formatCurrencyAmount } from "@/lib/utils";
import { useRegion } from "@/context/RegionContext";
import { getRegionConfigByCode } from "@/lib/regionConfig";

export interface CheckoutSidebarProps {
  car: PriceResultProps;
  total: PriceProps;
  dropOff?: StationResultProps;
  pickUp?: StationResultProps;
  extras: PriceResultAllExtra[];
  rentalDays: number;
  protection?: {
    name: string;
    isInclude: boolean;
    price: number;
    excess?: number;
  };
  discount?: {
    code: string;
    type: string;
    value: string;
    discount_amount: number;
    final_amount: number;
  } | null;
  delivery?: Array<{
    type: "pickup" | "return";
    address: string;
    fee: number;
    currency: string;
  }> | null;
}
const MAX_CHARGE_DAYS: Record<string, number> = {
  "NVS": 10,
  "BSEAT": 10,
  "CSEAT": 10,
  "YDS": 4,
};

const EXTRA_DESCRIPTIONS: Record<string, string> = {
  "YDS": "Young Driver Surcharge",
  "AHOUC": "After Hour Collection",
  "AHOUR": "After Hours Delivery",
  "NVS": "Navigation System",
  "BSEAT": "Baby Seat",
  "CSEAT": "Child Seat",
  "OW": "One Way Fee"
};


const CheckoutSidebar: React.FC<CheckoutSidebarProps> = memo(({
  car, 
  total, 
  dropOff, 
  pickUp,
  extras,
  rentalDays,
  protection,
  discount,
  delivery
}) => {
    const [searchParams] = useSearchParams();
    const { region } = useRegion();
    const basicProtectionExcess = getRegionConfigByCode(
      car._region ?? region.code,
    ).protection.basicExcess;
    
    // Memoize required extras calculation
    const requiredExtraIds = useMemo(() => 
      car?.extras?.required?.[0]?.split(',') || []
    , [car?.extras?.required]);
    
    // Only log when requiredExtraIds actually changes
    useMemo(() => {
      console.log(requiredExtraIds);
      return requiredExtraIds;
    }, [requiredExtraIds]);
    
    // Memoize filtered extras calculation
    const allExtras = useMemo(() => 
      extras.filter(extra => {
        // Include if it's a required extra
        const isRequired = requiredExtraIds.includes(extra.extra_id);
        // Include if it has a positive count
        const isSelected = extra.count && extra.count > 0;
        // Exclude hidden items unless they are required or selected
        const isHidden = HIDDEN_EXTRA_ITEMS.includes(extra.extra_id);
        // Include if either required or selected, and not hidden (unless required/selected)
        return (isRequired || isSelected) && (!isHidden || isRequired || isSelected);
      })
    , [extras, requiredExtraIds]);
    
    // Memoize extra totals calculation
    const { extraOneTimeChargeTotal, extraTimesRentalDaysTotal, extraTotal } = useMemo(() => {
      // Calculate one-time charge extras (excluding required extras)
      const oneTimeChargeTotal = extraTotalWithCountOnly(
          allExtras.filter((e) =>
              ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id) &&
              !requiredExtraIds.includes(e.extra_id)
          )
      );

      // Calculate rental days based extras (excluding required extras)
      const rentalDaysTotal = extraTotalWithRentalDays(
          allExtras.filter((e) =>
              !ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id) &&
              !requiredExtraIds.includes(e.extra_id)
          ),
          rentalDays
      );
      
      const total = oneTimeChargeTotal + rentalDaysTotal;
      
      return {
        extraOneTimeChargeTotal: oneTimeChargeTotal,
        extraTimesRentalDaysTotal: rentalDaysTotal,
        extraTotal: total
      };
    }, [allExtras, requiredExtraIds, rentalDays]);

    const deliveryTotal = delivery?.reduce((sum, leg) => sum + leg.fee, 0) ?? 0;

    return (
    <div className="rounded-md border-2 border-neutral-200 p-3 w-[400px]">
      <p className="mb-6 text-xl font-bold">Review</p>
      <div className="flex flex-row justify-between items-center pb-2 font-bold">
        <p className="w-48">Vehicle</p>
        <div className="flex items-center justify-end space-x-8">
          {/* <p className="w-12 text-center font-medium">1</p> */}
          <p className="w-24 text-right">
            {formatCurrencyAmount(car.pricing.total_with_tax*rentalDays, car.pricing.currency)}
          </p>
        </div>
      </div>
      <Divider />
      <div className="my-4 flex flex-col space-y-2">
        <div className="flex flex-row items-center justify-between">
          <p className="font-bold">{car.group_detail.name}</p>
          <img 
            src={car.group_detail.image_url} 
            alt={car.group_detail.name}
            className="w-1/4"
          />
        </div>
        <div className="rounded-full border-2 pl-3 pr-1 py-1 flex flex-row items-center space-x-2 w-fit">
          <span className="text-[12px] font-bold">OR SIMILAR ECONOMIQUE</span>
          <Tooltip 
            id="similar-info-checkout"
            title=""
            content={<p className="font-normal">The model you get on your pick up day is subject to availability.</p>}
          >
            <FontAwesomeIcon icon={faInfoCircle} className="text-neutral-300 text-[12px]" />
          </Tooltip>
        </div>
      </div>
      {/* Custom vehicle details with more spacing */}
      <div className="flex flex-row space-x-4 font-bold mb-3 pl-2">
        <div className="flex flex-row space-x-2 items-center">
          <img src="/icon/passenger.svg" />
          <span>{car.group_detail.seats}</span>
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <img src="/icon/gate.svg" />
          <span>{car.group_detail.doors}</span>
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <img src="/icon/luggage.svg" />
          <span>{car.group_detail.suitcases}</span>
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <img src="/icon/type.svg" />
          <span>{car.group_detail.is_automatic ? "A" : "M"}</span>
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <img src="/icon/fan.svg" />
          <span>A/C</span>
        </div>
        <div className="flex flex-row space-x-2 items-center">
          <img src="/icon/age.svg" />
          <span>{car.group_detail.min_age}</span>
        </div>
      </div>
      <div className="flex flex-row items-start justify-between text-sm">
        <p>
        <span className="font-bold">Basic rate for {rentalDays} day(s)</span> 
        </p>
        <div className="flex flex-col">
          <p>Included</p>
          <p className="text-neutral-500">
          {formatCurrencyAmount(car.pricing.total_with_tax*rentalDays, car.pricing.currency)}
          </p>
        </div>
      </div>
        <Accordion type="single" collapsible>
          <AccordionItem value="fuel-policy">
            <AccordionTrigger onClick={() => { }}>
              <p className="font-bold text-green-600">
                WHAT'S INCLUDED ? {' '}
                <FontAwesomeIcon icon={faChevronDown} />
              </p>
            </AccordionTrigger>
            <AccordionContent className="text-black text-sm -mt-1 font-normal">
              <div className="flex flex-row justify-between  font-light text-sm  ">
                <p>Mileage</p>
                <p>
                  Unlimited
                </p>
              </div>     
              <div className="flex flex-row justify-between  font-light text-sm  ">
                <p>SST <a href="/pages/terms-conditions/" className="font-bold text-green-600">See full T&C</a></p>
                <p>
                  8%
                </p>
              </div>             
              </AccordionContent>
          </AccordionItem>
        </Accordion>
      {/* <p className="mb-3 mt-2 text-sm font-bold text-green-600">
        WHAT'S INCLUDED ?
      </p> */}
      {searchParams.get("pickupLocation") && (
        <div className="bg-neutral-100 p-2 border-[1px] border-gray-300 ">
          <div className="mb-2 text-xs font-bold">Pick up</div>

          <div className="font-bold">{`${pickUp?.name}`}</div>
          <div className="text-sm text-neutral-500">{`${searchParams.get("pickupYear")}-${searchParams.get("pickupMonth")}-${searchParams.get("pickupDay")}   ${searchParams.get("pickupHour")}:${searchParams.get("pickupMinute")}`}</div>
        </div>
      )}
      {searchParams.get("dropoffLocation") && (
        <div className="bg-neutral-100 p-2 border-[1px] border-gray-300 ">
          <div className="mb-2 text-xs font-bold">Return</div>

          <div className="font-bold">{`${dropOff?.name}`}</div>
          <div className="text-sm text-neutral-500">{`${searchParams.get("dropoffYear")}-${searchParams.get("dropoffMonth")}-${searchParams.get("dropoffDay")}   ${searchParams.get("dropoffHour")}:${searchParams.get("dropoffMinute")}`}</div>
        </div>
      )}
      <div className="flex flex-row justify-between pb-2 font-bold mt-4">
        <p>Protections & Extras</p>
        <p>
        {formatCurrencyAmount(extraTotal, car.pricing.currency)}
        </p>
      </div>
      <Divider />

      <div className="flex flex-row items-end justify-between text-sm mt-2">
        <div className="flex flex-col">
          <div className="flex flex-row items-center space-x-2">
            <span className="font-bold">Basic</span>
            <Tooltip 
              id="basic-protection-info"
              title=""
              content={
                <div className="text-left w-full md:w-[500px] max-w-[calc(100vw-2rem)] font-normal">
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FontAwesomeIcon icon={faCheck} className="text-green-600 text-sm" />
                      <p className="font-bold">Collision Damage Protection</p>
                    </div>
                    <p className="text-sm pl-6">Your financial responsibility: {protection?.excess !== undefined && protection?.excess !== null ? formatCurrencyAmount(protection.excess, car.pricing.currency) : formatCurrencyAmount(basicProtectionExcess, car.pricing.currency)}</p>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FontAwesomeIcon icon={faCheck} className="text-green-600 text-sm" />
                      <p className="font-bold">Theft Protection</p>
                    </div>
                    <p className="text-sm pl-6">Your financial responsibility: {protection?.excess !== undefined && protection?.excess !== null ? formatCurrencyAmount(protection.excess, car.pricing.currency) : formatCurrencyAmount(basicProtectionExcess, car.pricing.currency)}</p>
                  </div>
                </div>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="text-neutral-300" />
            </Tooltip>
          </div>
          <p>For {rentalDays} day(s)</p>
        </div>
        <p>included</p>
      </div>
      {/* <div className="flex flex-row items-end justify-between text-sm">
        <div className="flex flex-col">
          <p className="font-bold">Extras</p>
        </div>
        <p></p>
      </div> */}

      {allExtras.map((extra, index) => {
          const isAddButtonOnly = extra.is_insurance || extra.extra_id === "NVS";
          const price = ONE_TIME_CHARGE_EXTRA_ITEMS.includes(extra.extra_id)
              ? extraPriceWithCountOnly(extra)
              : extraPriceWithRentalDays(extra, rentalDays);
          
          return (
              <div
                  key={`extra-${extra.extra_id}-${index}`}
                  className="flex flex-row items-end justify-between text-sm mt-4"
              >
                  <div className="flex flex-col">
                      <p className="font-bold">{EXTRA_DESCRIPTIONS[extra.extra_id] || extra.name}</p>
                      <p>{`For ${rentalDays} day(s)`}</p>
                  </div>
                  <div className="flex items-center justify-end space-x-8">
                      <p className="w-12 text-center font-medium">{isAddButtonOnly ? "1" : `${extra.count}`}</p>
                      <p className="w-24 text-right">
                          {formatCurrencyAmount(price, extra.currency)}
                      </p>
                  </div>
              </div>
          );
      })}
      {delivery && delivery.length > 0 && (
        <>
          <div className="flex flex-row justify-between pb-2 font-bold mt-6">
            <p>Delivery & Collection</p>
            <p>{formatCurrencyAmount(deliveryTotal, car.pricing.currency)}</p>
          </div>
          <Divider />
        </>
      )}
      {delivery && delivery.map((leg, index) => (
        <div key={`delivery-${leg.type}-${index}`} className="flex flex-row items-end justify-between text-sm mt-4">
          <div className="flex flex-col pr-2">
            <p className="font-bold">
              {leg.type === "pickup" ? "Delivery to your address" : "Collection from your address"}
            </p>
            <p className="text-neutral-500 break-words">{leg.address}</p>
          </div>
          <div className="flex items-center justify-end space-x-8">
            <p className="w-12 text-center font-medium">1</p>
            <p className="w-24 text-right">
              {formatCurrencyAmount(leg.fee, leg.currency)}
            </p>
          </div>
        </div>
      ))}
      {/* <div className="flex flex-row items-end justify-between text-sm">
        <div className="flex flex-col">
          <p className="font-bold">Basic</p>
          <p>For 12 day(s)</p>
        </div>
        <p>included</p>
      </div> */}
      {/* <Divider className="pt-2" />
      <div className="my-2 py-2 text-sm font-bold text-green-600">
        Add a coupon code
      </div> */}
      <Divider className="pb-2 mt-4" />
      {discount && (
        <>
          <div className="flex flex-row justify-between text-sm py-2">
            <div className="flex flex-col">
              <p className="font-bold text-green-600">Coupon ({discount.code})</p>
              <p className="text-xs text-neutral-500">
                {discount.type === "PERCENTAGE"
                  ? `${discount.value}% off`
                  : `${formatCurrencySymbol(car.pricing.currency)} ${discount.value} off`}
              </p>
            </div>
            <p className="text-green-600 font-bold">
              -{formatCurrencyAmount(discount.discount_amount, car.pricing.currency)}
            </p>
          </div>
          <Divider className="pb-2" />
        </>
      )}
      <div className="flex flex-row justify-between font-bold">
        <div className="w-48">
          <p>Total</p>
          <p className="text-xs font-normal">Due upon booking</p>
        </div>
        <div className="w-32 text-right">
          {discount ? (
            <div className="flex flex-col">
              <p className="text-sm line-through text-neutral-500">
                {formatCurrencyAmount(total.price, car.pricing.currency)}
              </p>
              <p className="text-green-600">
                {formatCurrencyAmount(discount.final_amount + deliveryTotal, car.pricing.currency)}
              </p>
            </div>
          ) : (
            <p>
              {formatCurrencyAmount(total.price, car.pricing.currency)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

export default CheckoutSidebar;
